import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { TrendingUp, TrendingDown, Minus, AlertTriangle, Sparkles } from "lucide-react";

/**
 * SkillHealthBadge — compact 0-100 score chip that reveals a full
 * trending / stable / declining / obsolete breakdown on hover.
 *
 * Data shape expected on ``health``:
 *   {
 *     score: number | null,
 *     breakdown: {
 *       trending: string[], stable: string[], rising: string[],
 *       declining: string[], obsolete: string[], unknown: string[],
 *     },
 *     required: string[],
 *     nice: string[],
 *     replacements: { [skill]: replacement_name },
 *   }
 *
 * Renders a subtle "—" when there is no data yet (job has no required or
 * nice-to-have skills tagged). Never crashes on partial data.
 */
export default function SkillHealthBadge({ health, testId }) {
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState(null);   // {top, left} — for portal
    const anchorRef = useRef(null);

    // Recompute portal position whenever the popover opens.
    useEffect(() => {
        if (!open || !anchorRef.current) return;
        const rect = anchorRef.current.getBoundingClientRect();
        setCoords({
            top: rect.bottom + 4,          // just below the anchor
            left: Math.max(8, Math.min(rect.right - 320, window.innerWidth - 328)),  // clamp to viewport
        });
    }, [open]);

    // Auto-close if the user clicks outside the anchor OR the popover
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (anchorRef.current && anchorRef.current.contains(e.target)) return;
            if (e.target.closest?.("[data-skill-health-popover]")) return;
            setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    if (!health || health.score === null || health.score === undefined) {
        return (
            <span className="text-[11px] font-mono text-foreground/40" data-testid={testId}>
                — no skills
            </span>
        );
    }

    const score = health.score;
    const tone =
        score >= 75 ? { border: "border-emerald-300", bg: "bg-emerald-50", text: "text-emerald-700" } :
        score >= 55 ? { border: "border-sky-300", bg: "bg-sky-50", text: "text-sky-700" } :
        score >= 35 ? { border: "border-amber-300", bg: "bg-amber-50", text: "text-amber-700" } :
        { border: "border-rose-300", bg: "bg-rose-50", text: "text-rose-700" };

    const b = health.breakdown || {};
    const nCounts = {
        trending: (b.trending || []).length + (b.rising || []).length,
        stable: (b.stable || []).length,
        declining: (b.declining || []).length,
        obsolete: (b.obsolete || []).length,
        unknown: (b.unknown || []).length,
    };
    const hasWarn = nCounts.declining > 0 || nCounts.obsolete > 0;

    return (
        <div
            ref={anchorRef}
            className="relative inline-block"
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            data-testid={testId}
        >
            <button
                type="button"
                className={`inline-flex items-center gap-1 h-6 px-1.5 border ${tone.border} ${tone.bg} ${tone.text} text-[11px] font-mono cursor-help`}
                aria-describedby={testId ? `${testId}-popover` : undefined}
                onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
            >
                <Sparkles className="w-3 h-3" />
                <span className="tabular-nums font-medium">{score}</span>
                {hasWarn && <AlertTriangle className="w-3 h-3 text-amber-700" />}
            </button>
            {open && coords && createPortal(
                <div
                    data-skill-health-popover
                    id={testId ? `${testId}-popover` : undefined}
                    role="tooltip"
                    className="fixed z-[9999] w-80 bg-white border border-border shadow-lg p-3 text-left"
                    style={{ top: coords.top, left: coords.left }}
                    onMouseEnter={() => setOpen(true)}
                    onMouseLeave={() => setOpen(false)}
                    data-testid={testId ? `${testId}-popover` : undefined}
                >
                    <div className="flex items-center gap-2 mb-2">
                        <span className={`inline-flex items-center gap-1 px-1.5 border ${tone.border} ${tone.bg} ${tone.text} text-[11px] font-mono`}>
                            <Sparkles className="w-3 h-3" /> {score}
                        </span>
                        <span className="text-[11px] font-mono uppercase tracking-widest text-foreground/70">Skill trend score</span>
                    </div>
                    <p className="text-[11px] text-foreground/70 mb-3 leading-snug">
                        Weighted classification of this JD&apos;s skills. Required skills count 2× nice-to-haves.
                    </p>

                    <SkillGroup icon={TrendingUp} label="Trending / Rising" tone="emerald" items={[...(b.trending || []), ...(b.rising || [])]} testId={testId ? `${testId}-trending` : undefined} />
                    <SkillGroup icon={Minus} label="Stable" tone="sky" items={b.stable || []} testId={testId ? `${testId}-stable` : undefined} />
                    <SkillGroup icon={TrendingDown} label="Declining" tone="amber" items={b.declining || []} replacements={health.replacements} testId={testId ? `${testId}-declining` : undefined} />
                    <SkillGroup icon={AlertTriangle} label="Obsolete" tone="rose" items={b.obsolete || []} replacements={health.replacements} testId={testId ? `${testId}-obsolete` : undefined} />
                    {(b.unknown || []).length > 0 && (
                        <SkillGroup label="Untagged (not yet in Skill Center)" tone="slate" items={b.unknown || []} testId={testId ? `${testId}-unknown` : undefined} muted />
                    )}
                    <div className="mt-2 pt-2 border-t border-border text-[10px] font-mono text-foreground/60 flex items-center gap-3">
                        <span>{(health.required || []).length} required</span>
                        <span>·</span>
                        <span>{(health.nice || []).length} nice-to-have</span>
                    </div>
                </div>,
                document.body,
            )}
        </div>
    );
}

const TONE_CLS = {
    emerald: { border: "border-emerald-300", bg: "bg-emerald-50", text: "text-emerald-700" },
    sky:     { border: "border-sky-300", bg: "bg-sky-50", text: "text-sky-700" },
    amber:   { border: "border-amber-300", bg: "bg-amber-50", text: "text-amber-800" },
    rose:    { border: "border-rose-300", bg: "bg-rose-50", text: "text-rose-700" },
    slate:   { border: "border-border", bg: "bg-secondary/40", text: "text-foreground/60" },
};

function SkillGroup({ icon: Icon, label, items, tone, replacements = {}, testId, muted = false }) {
    if (!items || items.length === 0) return null;
    const t = TONE_CLS[tone] || TONE_CLS.slate;
    return (
        <div className={`mb-2 ${muted ? "opacity-70" : ""}`} data-testid={testId}>
            <div className={`flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest ${t.text} mb-1`}>
                {Icon && <Icon className="w-3 h-3" />}
                <span>{label}</span>
                <span className="tabular-nums ml-1">({items.length})</span>
            </div>
            <div className="flex items-center gap-1 flex-wrap">
                {items.map((s) => {
                    const replacement = replacements?.[s];
                    return (
                        <span key={s} className={`inline-flex items-center gap-1 border ${t.border} ${t.bg} ${t.text} px-1.5 py-0.5 text-[11px] font-mono`}>
                            {s}
                            {replacement && (
                                <span className="text-foreground/60 lowercase" title={`Replace with ${replacement}`}>
                                    → {replacement}
                                </span>
                            )}
                        </span>
                    );
                })}
            </div>
        </div>
    );
}
